import {
    DashboardOutlined,
    DescriptionOutlined,
    ExtensionOutlined,
    ReceiptOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, useMediaQuery, useTheme, Divider, Box, Tooltip } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useDocumentFocus from '../../hooks/useDocumentFocus';
import useDashboardService from '../../services/dashboard.service';
interface MenuItem {
    text: string;
    icon: JSX.Element;
    path: string;
}

interface MainMenuProps {
    onNavigate?: () => void;
    isDisabled: boolean;
}

const getMenuItems = (t: (key: string) => string): MenuItem[] => [
    { text: t('sidebar.menu.dashboard'), icon: <DashboardOutlined />, path: '/dashboard' },
    { text: t('sidebar.menu.integrations'), icon: <ExtensionOutlined />, path: '/integrations' },
    { text: t('sidebar.menu.transactions'), icon: <ReceiptOutlined />, path: '/transactions' },
    { text: t('sidebar.menu.taxReports'), icon: <DescriptionOutlined />, path: '/tax-reports' },
];

const MainMenu: React.FC<MainMenuProps> = ({ onNavigate, isDisabled }) => {
    const { t } = useTranslation();
    const menuItems = getMenuItems(t);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const dashboardService = useDashboardService();
    const { currentCtUser } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // ==============================
    // Refresh balance every 60 seconds
    // ==============================
    const isFocused = useDocumentFocus();

    useQuery({
        queryKey: ['refreshBalance'],
        queryFn: dashboardService.refreshBalance,
        enabled: !!currentCtUser,
    });

    useQuery({
        queryKey: ['refreshBalanceInterval'],
        queryFn: () => {
            queryClient.invalidateQueries({ queryKey: ['refreshBalance'] });
            return null;
        },
        refetchInterval: 60000,
        enabled: isFocused && !!currentCtUser,
    });
    // ==============================
    // Refresh balance every 60 seconds
    // ==============================

    const handleNavigation = (path: string) => {
        navigate(path);
        onNavigate?.();
    };

    const renderMenuItem = (item: MenuItem) => {
        const isSelected = location.pathname === item.path;
        // Never disable integrations menu item
        const isItemDisabled = isDisabled && !isSelected && item.path !== '/integrations';

        const menuItemContent = (
            <ListItem 
                key={item.text} 
                disablePadding 
                sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    color: (theme) => theme.palette.text.secondary,
                    position: 'relative',
                }}
            >
                <ListItemButton
                    onClick={() => !isItemDisabled && handleNavigation(item.path)}
                    selected={isSelected}
                    disabled={isItemDisabled}
                    sx={{
                        borderRadius: 1,
                        mb: 0.75,
                        py: isMobile ? 1.5 : 1.25,
                        px: 1.5,
                        color: (theme) => theme.palette.mode === 'light' ? '#212529' : theme.palette.text.secondary,
                        position: 'relative',
                        ...(isSelected && !isItemDisabled && {
                            color: (theme) => theme.palette.mode === 'light' ? '#212529' : theme.palette.text.primary,
                            fontWeight: 500,
                            bgcolor: (theme) => theme.palette.mode === 'light' ? '#d1d5db' : '#252525',
                        }),
                        ...(isItemDisabled && {
                            color: (theme) => theme.palette.action.disabled,
                            opacity: 0.6,
                            cursor: 'not-allowed',
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: (theme) => theme.palette.action.disabled,
                            },
                        }),
                        '&.Mui-selected': {
                            bgcolor: (theme) => theme.palette.mode === 'light' ? '#d1d5db' : '#252525',
                            '&:hover': {
                                bgcolor: isItemDisabled ? 'transparent' : theme.palette.primary.main,
                                color: isItemDisabled ? (theme) => theme.palette.action.disabled : '#ffffff',
                            },
                        },
                        '&:hover': {
                            bgcolor: isItemDisabled ? 'transparent' : theme.palette.primary.main,
                            color: isItemDisabled ? (theme) => theme.palette.action.disabled : '#ffffff',
                        },
                    }}
                >
                    <ListItemIcon 
                        sx={{
                            minWidth: 34,
                            color: 'inherit',
                            '& .MuiSvgIcon-root': {
                                fontSize: '1.4rem',
                            },
                        }}
                    >
                        {item.icon}
                    </ListItemIcon>
                    <ListItemText
                        primary={item.text}
                        slotProps={{
                            primary: {
                                sx: {
                                    fontSize: '0.875rem',
                                    fontWeight: isSelected ? 700 : 600,
                                    letterSpacing: '0.01em',
                                },
                            },
                        }}
                    />
                </ListItemButton>
            </ListItem>
        );

        // Wrap with tooltip if disabled
        if (isItemDisabled) {
            return (
                <Tooltip
                    key={item.text}
                    title={t('sidebar.tooltips.addIntegrationFirst')}
                    placement="right"
                    arrow
                >
                    <div>
                        {menuItemContent}
                    </div>
                </Tooltip>
            );
        }

        return menuItemContent;
    };

    return (
        <>
            <List sx={{
                px: 1.5,
                py: 1,
                mt: isMobile ? 1 : 0,
            }}>
                {menuItems.map(renderMenuItem)}
            </List>
            
            {/* Add divider after Steuerberichte */}
            <Box sx={{ px: 1.5, mt: 1, mb: 1 }}>
                <Divider />
            </Box>
        </>
    );
};

export default MainMenu;
